import templateUrl from './edit-onboarding-application.component.html'
import { ImageCropperModal } from '../../../shared/image-cropper/image-cropper-modal.component';
import { IQService } from 'angular';
import { EmployeeService, IcdOfficeService, FileService, OnboardingApplicationService } from '../../../../core/services';
import { UtilityService } from '../../../../core/services/utility.service';

export interface Applicant {
    FirstName: string;
    LastName: string;
    UserName: string;
    EmailAddress: string;
    CellPhone: {
        PhoneNumber: string;
        Extension: string | undefined;
    };
    Password: string;
    DateOfBirth: string;
}

export interface OnboardingApplication {
    OnboardingApplicationID: number;
    IsSelfSignUp: any;
    Applicant: Applicant;
    IcdOfficeID: string;
    DivisionID: string;
    OnboardingDocuments: any;
    InitialOnboardingApplicationStatusID: string;
    Password: string;
}

export const EditOnboardingApplicationComponent = {
    templateUrl,
    bindings: {
        onboardingApplication: "<",
        showPasswordEditor: "<",
        afterSubmit: "&"
    },
    controllerAs: "vm",
    controller: class EditOnboardingApplicationController {
        isSiteAdmin: boolean;

        constructor(private $uibModal, private $q: IQService, private employeeService: EmployeeService,
            private icdOfficeService: IcdOfficeService, private onboardingApplicationService:
                OnboardingApplicationService, private onboardingDivisionService,
            private utilityService: UtilityService,
            private fileService: FileService) {
            'ngInject'
        }

        afterSubmit: any;
        divisions = []; // The list of divisions that can be selected.
        editForm: any;
        onboardingApplication: any;
        onboardingContacts: any;
        icdOffices: any;
        applicant: Applicant;

        $onInit() {
            window.onbeforeunload = this.onBeforeUnload;

            this.onboardingApplication = this.onboardingApplication
                || this.onboardingApplicationService.createOnboardingApplicationVM();
            this.utilityService.showLoading();
            this.loadInitialData()
                .catch(ex => {
                    console.error(ex);
                    alert("Initial load failed: " + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => {
                    this.utilityService.hideLoading();
                });
        }

        onChangeIcdOffice() {
            if (this.editForm) {
                this.editForm.IcdOffice.$setDirty(true);
            }
        }

        onChangeEmailAddress() {
            // Check availability of the entered email address.
            if (this.editForm && this.editForm.UserName) {
                this.editForm.UserName.$setValidity("available", true);
                if (this.onboardingApplication.IsSelfSignUp) {
                    this.editForm.UserName.$setValidity("pending", false);

                    this.employeeService.isEmailAddressAvailable(this.onboardingApplication.Applicant.UserName)
                        .then(available => {
                            this.editForm.UserName.$setValidity("available", available);
                        })
                        .catch(() => { })
                        .then(() => {
                            // Clear the "pending" validation; this is just to keep the form from being submitted
                            // while waiting for the API.
                            this.editForm.UserName.$setValidity("pending", true);
                        });
                }
            }
        }

        onChangeDivision() {
            if (this.editForm) {
                this.editForm.Division.$setDirty(true);
            }

            if (this.onboardingApplication.Division) {
                this.selectInitialApplicationStatus(this.onboardingApplication,
                    this.onboardingApplication.Division.OnboardingPolicy.ApplicationStatuses);
                if (this.onboardingApplication.IsSelfSignUp !== null) {
                    // Stay the same or disable self sign up.
                    this.onboardingApplication.IsSelfSignUp =
                        this.onboardingApplication.Division.OnboardingPolicy.EnableSelfSignUp
                        && this.onboardingApplication.IsSelfSignUp;
                }
            } else {
                this.onboardingApplication.IsSelfSignUp = null;
            }

            this.utilityService.showLoading();
            this.updateValidIcdOffices(this.onboardingApplication.Division)
                .then(() => {
                    this.selectApplicationIcdOffice(this.onboardingApplication, this.icdOffices);
                })
                .catch(ex => {
                    console.error(ex);
                    alert("Loading details for division failed: " + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => {
                    this.utilityService.hideLoading();
                });
        }

        onChangeInitialStatus() {
            if (this.editForm) {
                this.editForm.InitialApplicationStatus.$setDirty(true);
            }
        }

        onChangeSelfSignUp() {
            if (this.editForm) {
                // Re-check validation on email address and re-select default initial status.
                this.onChangeEmailAddress();
                if (this.onboardingApplication.Division) {
                    this.selectInitialApplicationStatus(this.onboardingApplication,
                        this.onboardingApplication.Division.OnboardingPolicy.ApplicationStatuses);
                }
            }
        }

        onSubmit() {
            this.utilityService.showLoading();

            let saveApplication;
            this.applicant = {
                FirstName: this.onboardingApplication.Applicant.FirstName,
                LastName: this.onboardingApplication.Applicant.LastName,
                UserName: this.onboardingApplication.Applicant.UserName,
                EmailAddress: this.onboardingApplication.Applicant.EmailAddress,
                CellPhone: !this.onboardingApplication.Applicant.CellPhone ? null : {
                    PhoneNumber: this.onboardingApplication.Applicant.CellPhone.PhoneNumber,
                    Extension: null
                },
                Password: this.onboardingApplication.Password,
                DateOfBirth: this.onboardingApplication.Applicant.DateOfBirth
            };
            return this.$q.when()
                .then(() => {
                    let request;
                    if (this.onboardingApplication.OnboardingApplicationID) {
                        request = {
                            OnboardingApplicationID: this.onboardingApplication.OnboardingApplicationID,
                            IsSelfSignUp: this.onboardingApplication.IsSelfSignUp,
                            FirstName: this.onboardingApplication.Applicant.FirstName,
                            LastName: this.onboardingApplication.Applicant.LastName,
                            UserName: this.onboardingApplication.Applicant.UserName,
                            EmailAddress: this.onboardingApplication.Applicant.EmailAddress,
                            CellPhone: !this.onboardingApplication.Applicant.CellPhone ? null : {
                                PhoneNumber: this.onboardingApplication.Applicant.CellPhone.PhoneNumber,
                                Extension: null
                            },
                            Password: this.onboardingApplication.Password,
                            DateOfBirth: this.onboardingApplication.Applicant.DateOfBirth,
                            IcdOfficeID: this.onboardingApplication.IcdOffice.Id
                        };

                        // Application already exists, so it is being updated.
                        saveApplication = this.onboardingApplicationService.patchOnboardingApplication(
                            this.onboardingApplication.OnboardingApplicationID,
                            request);
                    } else {
                        request = {
                            IsSelfSignUp: this.onboardingApplication.IsSelfSignUp,
                            DivisionID: this.onboardingApplication.Division.DivisionID,
                            IcdOfficeID: this.onboardingApplication.IcdOffice.Id,
                            Applicant: this.applicant,
                            OnboardingDocuments: this.onboardingApplication.OnboardingDocuments,
                            InitialOnboardingApplicationStatusID: this.onboardingApplication
                                .InitialApplicationStatus.OnboardingApplicationStatusID,
                            Password: this.onboardingApplication.Password
                        };
                        saveApplication = this.onboardingApplicationService.createOnboardingApplication(
                            request);
                    }

                    return saveApplication
                        .then(() => {
                            this.editForm.$setPristine();
                            this.afterSubmit({ onboardingApplication: this.onboardingApplication, window: window });
                        });
                })
                .catch(ex => {
                    console.error(ex);
                    alert("Save failed: " + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => {
                    this.utilityService.hideLoading();
                });
        }

        /**
         * Load initial data from the data services
         * @returns {Promise} Resolves when all data operations are resolved into the controller.
         */
        loadInitialData() {
            // Load both the list of all onboarding divisions and the list of filtered divisions.
            const onboardingDivisions = this.getOnboardingDivisions()
                .then(divisions => {
                    this.divisions = divisions;
                    return this.divisions;
                });

            const identityLoaded = this.employeeService.retrieveIdentity()
                .then(identity => this.isSiteAdmin = identity.IsSiteAdmin);

            // Create a new application if none was passed in.
            const onboardingApplication = this.onboardingApplication;

            const divisionsLoaded = onboardingDivisions
                .then(divisions => {
                    // Skip loading additional data if no divisions were returned. At least one onboarding division
                    // and an office is required in order to submit an application.
                    if (!divisions.length) return;
                    this.selectApplicationDivision(onboardingApplication, divisions);

                    if (!this.onboardingApplication.Division) return;
                    // Overwrite null value with division value.
                    this.selectInitialApplicationStatus(this.onboardingApplication,
                        this.onboardingApplication.Division.OnboardingPolicy.ApplicationStatuses);

                    // Load ICD Offices based on currently selected division.
                    return this.updateValidIcdOffices(onboardingApplication.Division);
                })
                .then(() => {
                    if (!this.icdOffices) return;
                    this.selectApplicationIcdOffice(onboardingApplication, this.icdOffices);
                    this.onboardingApplication = onboardingApplication;
                });
            return this.$q.all([identityLoaded, divisionsLoaded])
        }

        updateValidIcdOffices(division) {
            let icdOffices;
            if (division) {
                icdOffices = this.getIcdOffices(division.DivisionID);
            } else {
                icdOffices = this.$q.when([]);
            }
            return icdOffices.then(icdOffices => {
                this.icdOffices = icdOffices;
            });
        }

        /**
         * Resolve the list of all ICD offices that the current user can access.
         * @param {Number} divisionID The division whose offices will be returned.
         * @returns {Promise<any[]>} Resolve a list of all ICD offices.
         */
        getIcdOffices(divisionID) {
            return this.icdOfficeService.retrieveIcdOffices({
                divisionID: divisionID
            })
                .then(icdOffices => {
                    return icdOffices.map(icdOffice => {
                        // Compile ICD and office name into single text field.
                        icdOffice.OfficeName = kendo.format("{0} - {1}", icdOffice.IcdCorporationName, icdOffice.OfficeName);
                        return icdOffice;
                    }).sort((a, b) => {
                        // Sort by the new text field.
                        return a.OfficeName < b.OfficeName ? -1 : 1;
                    });
                });
        }

        /**
         * Resolve all onboarding divisions.
         * @returns {Promise<any[]>} Resolve a list of all onboarding divisions.
         */
        getOnboardingDivisions() {
            return this.onboardingDivisionService.retrieveOnboardingDivisions()
                .then(divisions => {
                    // Map the division's onboarding policy data into a view model-friendly format.
                    return divisions.map(division => {
                        // Group policy documents by group while maintaining the same logical order.
                        const policy = division.OnboardingPolicy;

                        policy.OnboardingDocuments.forEach((document, i) => {
                            // Assign a sort index to the document so it can be mapped back to the request.
                            document.sortIndex = i;
                        });

                        policy.documentSections = [{
                            title: 'Required Uploads',
                            required: true,
                            documentGroups: this.groupOnboardingDocumentByDocumentGroup(
                                policy.OnboardingDocuments.filter(document => {
                                    return document.IsRequired;
                                }),
                                policy.OnboardingDocumentGroups)
                        }, {
                            title: 'Optional Uploads',
                            required: false,
                            documentGroups: this.groupOnboardingDocumentByDocumentGroup(
                                policy.OnboardingDocuments.filter(document => {
                                    return !document.IsRequired;
                                }),
                                policy.OnboardingDocumentGroups)
                        }].filter(section => {
                            return section.documentGroups.length > 0;
                        });

                        policy.initialApplicationStatuses = policy.ApplicationStatuses.filter(status => {
                            return status.StatusType === "Initial";
                        });

                        return division;
                    });
                });
        }

        /**
         * Select an appropriate ICD Office from the list for the given onboarding application.
         * @param {any} onboardingApplication The onboarding application response.
         * @param {any[]} icdOffices The list of offices to select from.
         */
        selectApplicationIcdOffice(onboardingApplication, icdOffices) {
            // Assign an office from the list. The references must be the same for the purpose of the select box.
            if (!onboardingApplication.OnboardingApplicationID) {
                // Not editing an existing application, so select an office from the list.
                if (icdOffices.length === 1 && !onboardingApplication.IcdOffice) {
                    // Pre-assign to the only office in the list.
                    onboardingApplication.IcdOffice = icdOffices[0];
                }
            } else if (onboardingApplication.IcdOfficeID) {
                // Load the office corresponding to the IcdOfficeID.
                onboardingApplication.IcdOffice = icdOffices.filter(icdOffice => {
                    return icdOffice.Id === onboardingApplication.IcdOfficeID;
                })[0] || null;
            }
        }

        /**
         * Select an appropriate initial status from the list for the given onboarding application.
         * @param {any} application The onboarding application
         * @param {any[]} statuses The list of available statuses.
         */
        selectInitialApplicationStatus(application, statuses) {
            const initialStatuses = statuses.filter(status => {
                return application.IsSelfSignUp ? status.StatusType === "SelfSignUp" : status.StatusType === "Initial";
            });
            application.InitialApplicationStatus = initialStatuses.length === 1
                ? initialStatuses[0] : null;
        }

        /**
         * Load divisions for the given onboarding application and assign a default
         * division if there is only one division at the office.
         * @param {any} onboardingApplication The onboarding application.
         * @param {any[]} divisions The list of divisions available to choose from.
         */
        selectApplicationDivision(onboardingApplication, divisions) {
            if (!onboardingApplication.OnboardingApplicationID) {
                if (divisions.length === 1 && !onboardingApplication.Division) {
                    onboardingApplication.Division = divisions[0];
                }
            } else if (onboardingApplication.DivisionID) {
                onboardingApplication.Division = divisions.filter(division => {
                    return division.DivisionID === onboardingApplication.DivisionID;
                })[0] || null;
            }
        }

        /**
         * Group the given list on onboarding document policies by their associated document group, if any.
         * @param {any} onboardingDocuments
         * @param {any} onboardingDocumentGroups
         */
        groupOnboardingDocumentByDocumentGroup(onboardingDocuments, onboardingDocumentGroups) {
            return onboardingDocuments
                .reduce((grouped, document, i) => {
                    const policyID = document.OnboardingPolicyDocumentGroupID;
                    if (!grouped[policyID]) {
                        // Maintain both an index and an ordered array of the document groups on the aggregate.
                        const group = {
                            OnboardingPolicyDocumentGroupID: policyID,
                            documents: [document]
                        };
                        if (policyID) {
                            // Future documents matching this policy will be inserted under the same group.
                            grouped[policyID] = group;
                        }
                        // Otherwise, groupless entries get inserted in the order they would normally be
                        // without grouping.
                        grouped.documentGroups.push(group);
                    } else {
                        grouped[policyID].documents.push(document);
                    }

                    return grouped;
                }, { documentGroups: [] })
                .documentGroups
                .map(documentGroup => {
                    documentGroup.policy = onboardingDocumentGroups.filter(group => {
                        return group.OnboardingPolicyDocumentGroupID === documentGroup.OnboardingPolicyDocumentGroupID;
                    })[0];
                    return documentGroup;
                });

        }

        onBeforeUnload() {
            if (this.editForm && this.editForm.$dirty) {
                return "Any unsaved changes will be discarded.";
            }
        }

        handleFileSelect(doc, value) {
            if (this.fileService.isValidImage(value) && doc.UseImageCropper) {
                this.fileService.fileToDataUrl(value)
                    .then(dataUrl => {
                        return this.$uibModal.open({
                            ...ImageCropperModal,
                            resolve: {
                                dataUrl: () => dataUrl,
                                cropHeight: () => 800,
                                cropWidth: () => 800,
                                resultHeight: () => 800,
                                resultWidth: () => 800,
                                areaType: () => "rectangle"
                            }
                        }).result
                            .then(data => {
                                if (!data) {
                                    return this.$q.when(null);
                                }
                                return this.fileService.dataUrlToFile(data, value.name, value.type)
                            }).then(file => {
                                if (!file) {
                                    const index = this.onboardingApplication.OnboardingDocuments
                                        .indexOf(this.onboardingApplication.OnboardingDocuments[doc.sortIndex]);
                                    if (index > -1) {
                                        this.onboardingApplication.OnboardingDocuments.splice(index, 1);
                                    }
                                } else {
                                    this.onboardingApplication.OnboardingDocuments[doc.sortIndex] = file;
                                }
                            });
                    });
            }
        }
    }
}
